import { useState, useEffect, useCallback } from 'react';
import { AtSign, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isReservedUsername, validateUsernameFormat } from '../utils/reserved-usernames';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, ApiError } from '../lib/api';

interface UsernameModalProps {
  isOpen: boolean;
  onComplete: (username: string, seededFields?: { display_name?: string | null; avatar_url?: string | null }) => void;
}

export function UsernameModal({ isOpen, onComplete }: UsernameModalProps) {
  const { session } = useAuth();
  const [username, setUsername] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Debounced username availability check
  useEffect(() => {
    if (!username) {
      setAvailable(null);
      setError(null);
      return;
    }

    // First validate format
    const formatValidation = validateUsernameFormat(username);
    if (!formatValidation.valid) {
      setError(formatValidation.error || 'Invalid username');
      setAvailable(false);
      return;
    }

    // Check reserved usernames
    if (isReservedUsername(username)) {
      setError('This username is reserved');
      setAvailable(false);
      return;
    }

    // Debounce the database check
    setChecking(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        // Use profiles_public for availability check (always readable)
        const { data, error: queryError } = await supabase
          .from('profiles_public')
          .select('id')
          .eq('username_lower', username.toLowerCase())
          .maybeSingle();

        if (queryError) {
          console.error('[Username] Check failed:', queryError);
          setError('Failed to check availability');
          setAvailable(null);
        } else if (data) {
          setAvailable(false);
          setError('This username is already taken');
        } else {
          setAvailable(true);
          setError(null);
        }
      } catch (err) {
        console.error('[Username] Check error:', err);
        setError('Failed to check availability');
        setAvailable(null);
      } finally {
        setChecking(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [username]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!available || saving) return;

    setSaving(true);
    setError(null);

    try {
      const token = session?.access_token;
      if (!token) {
        setError('Session expired. Please sign in again.');
        return;
      }

      // Claim username via worker endpoint (server-side validation + CIO identify)
      const result = await apiFetch<{ success: boolean; username: string; display_name?: string | null; avatar_url?: string | null }>('/user/claim-username', {
        method: 'POST',
        token,
        body: { username },
      });

      onComplete(username, {
        display_name: result?.display_name ?? null,
        avatar_url: result?.avatar_url ?? null,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'USERNAME_TAKEN') {
          setError('This username was just taken. Please try another.');
          setAvailable(false);
        } else {
          console.error('[Username] Save failed:', err);
          setError(err.message || 'Failed to save username. Please try again.');
        }
      } else {
        console.error('[Username] Save error:', err);
        setError('An unexpected error occurred');
      }
    } finally {
      setSaving(false);
    }
  }, [username, available, saving, session, onComplete]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - no onClick to close since this is required */}
      <div className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-[#0F1419] rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl shadow-black/50 ring-1 ring-white/[0.06]">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/25">
            <AtSign size={32} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">Choose Your Username</h2>
          <p className="text-slate-400 mt-2">
            Pick a unique username for your Filmple profile. This is how others will find you.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username Input */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-slate-300 mb-1">
              Username
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">@</span>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                placeholder="your_username"
                maxLength={20}
                autoFocus
                className="w-full pl-8 pr-10 py-2.5 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
              />
              {/* Status indicator */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checking && <Loader2 size={18} className="animate-spin text-slate-400" />}
                {!checking && available === true && <CheckCircle size={18} className="text-green-400" />}
                {!checking && available === false && <AlertCircle size={18} className="text-red-400" />}
              </div>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              3-20 characters. Letters, numbers, and underscores only.
            </p>
          </div>

          {/* Error/Availability message */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {!error && available === true && (
            <div className="p-3 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400">
              <CheckCircle size={18} />
              <span className="text-sm">@{username} is available!</span>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={!available || saving}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:from-cyan-400 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {saving && <Loader2 size={18} className="animate-spin" />}
            {saving ? 'Saving...' : 'Claim Username'}
          </button>
        </form>

        {/* Note */}
        <p className="mt-4 text-center text-xs text-slate-500">
          You can change your display name later, but your username is permanent.
        </p>
      </div>
    </div>
  );
}
